import { useEffect } from 'react';

import { cn } from 'helpers/classnames';
import { SmallLoader } from 'components/Loader';
import ShippingForm from 'components/account/ShippingForm';
import PickUpReturnDetails from 'components/account/PickUpReturnDetails';
import { PROVIDE_LABEL_OPTIONS } from 'constants/exchanges';
import { track } from 'apis/amethyst';
import { evReturnTransportationOptions } from 'events/returns';

import css from 'styles/containers/returns/shippingMethodChooser.scss';

const PrintLabelOption = ({ labelDelivery, onLabelDeliverySelect }) => (
  <>
    <input
      checked={labelDelivery === PROVIDE_LABEL_OPTIONS.PRINT}
      onClick={onLabelDeliverySelect}
      readOnly={true}
      type="radio"
      id="print"
      name="returnLabel"
      value={PROVIDE_LABEL_OPTIONS.PRINT}
    />
    <label htmlFor="print" className={cn(css.optionTitle, 'zappos:body-m')}>
      Print and email my return label
    </label>
    <p className={cn(css.optionInfo, 'zappos:body-m')}>Print a return label from home and return your item at your convenience.</p>
  </>
);

const LabelessLabelOption = ({ onLabelDeliverySelect, showLabellessReturns }) =>
  showLabellessReturns && (
    <>
      <input onClick={onLabelDeliverySelect} type="radio" id="labelless" name="returnLabel" value={PROVIDE_LABEL_OPTIONS.LABELLESS} />
      <label htmlFor="labelless" className={cn(css.optionTitle, 'zappos:body-m')}>
        FREE UPS drop-off (no printer needed)
      </label>
      <p className={cn(css.optionInfo, 'zappos:body-m')}>Show your return code to The UPS Store associate and they’ll print a FREE return label.</p>
    </>
  );

const PickupOption = ({ onLabelDeliverySelect, labelDelivery, UPSPickUpAddress }) => (
  <>
    <input onClick={onLabelDeliverySelect} type="radio" id="pickup" name="returnLabel" value={PROVIDE_LABEL_OPTIONS.PICKUP} />
    <label htmlFor="pickup" className={cn(css.optionTitle, 'zappos:body-m')}>
      FREE UPS pick-up (no printer needed)
    </label>
    <p className={cn(css.optionInfo, 'zappos:body-m')}>
      UPS will pick-up your return package the next business day. UPS asks that you be home for them to receive your package.
    </p>
    {labelDelivery === PROVIDE_LABEL_OPTIONS.PICKUP && <PickUpReturnDetails className={css.address} UPSPickUpAddress={UPSPickUpAddress} />}
  </>
);

const MailLabelOption = ({
  shipping,
  labelDelivery,
  showMailedLabelOption,
  onLabelDeliverySelect,
  onUpdateShippingPhone,
  onCancelReturn,
  onUpdateMailingAddressOrName,
  onSubmitReturn,
  isStoreCreditEnabled,
  labelPaymentType,
  zeroOriginalPaymentBalance,
  afterPayOrder
}) =>
  showMailedLabelOption && (
    <>
      <button type="button" onClick={onLabelDeliverySelect} value={PROVIDE_LABEL_OPTIONS.MAIL}>
        Don't have a printer?
      </button>
      {labelDelivery === PROVIDE_LABEL_OPTIONS.MAIL && (
        <div className={css.snail}>
          <input
            defaultChecked={labelDelivery === PROVIDE_LABEL_OPTIONS.MAIL}
            type="radio"
            id="mail"
            value={PROVIDE_LABEL_OPTIONS.MAIL}
            name="returnLabel"
          />
          <label htmlFor="mail" className={cn(css.optionTitle, 'zappos:body-m')}>
            Mail my label
          </label>
          <p className="zappos:body-m">You will be receiving your label by USPS mail in 4-7 days at this address</p>
          <ShippingForm
            isReducedInfo={true}
            primaryVerbiage="Submit Return"
            shipping={shipping}
            onChangeShippingAddress={onSubmitReturn}
            onUpdateMailingAddressOrName={onUpdateMailingAddressOrName}
            onUpdateShippingPhone={onUpdateShippingPhone}
            onCancelClick={onCancelReturn}
            onResetShippingForm={f => f}
            isStoreCreditEnabled={isStoreCreditEnabled}
            labelPaymentType={labelPaymentType}
            zeroOriginalPaymentBalance={zeroOriginalPaymentBalance}
            afterPayOrder={afterPayOrder}
          />
        </div>
      )}
    </>
  );

const SHIPPING_METHOD_OPTIONS = {
  [PROVIDE_LABEL_OPTIONS.PRINT]: PrintLabelOption,
  [PROVIDE_LABEL_OPTIONS.PICKUP]: PickupOption,
  [PROVIDE_LABEL_OPTIONS.LABELLESS]: LabelessLabelOption,
  [PROVIDE_LABEL_OPTIONS.MAIL]: MailLabelOption
};

const backupLabelMethods = [PROVIDE_LABEL_OPTIONS.PRINT, PROVIDE_LABEL_OPTIONS.LABELLESS, PROVIDE_LABEL_OPTIONS.MAIL];

const ShippingMethodChooser = ({
  shipping,
  labelDelivery,
  UPSPickUpAddress,
  showMailedLabelOption = false,
  showLabellessReturns = false,
  options,
  isLoading,
  showAvailableReturnLabels,
  arePickupReturnsAuthorized = false,
  onLabelDeliverySelect,
  onUpdateShippingPhone,
  onCancelReturn,
  onUpdateMailingAddressOrName,
  onSubmitReturn,
  isPrivateLabel,
  isStoreCreditEnabled,
  labelPaymentType,
  zeroOriginalPaymentBalance,
  afterPayOrder
}) => {
  const optionProps = {
    shipping,
    labelDelivery,
    UPSPickUpAddress,
    showMailedLabelOption,
    showLabellessReturns,
    onLabelDeliverySelect,
    onUpdateShippingPhone,
    onCancelReturn,
    onUpdateMailingAddressOrName,
    onSubmitReturn,
    isStoreCreditEnabled,
    labelPaymentType,
    zeroOriginalPaymentBalance,
    afterPayOrder
  };
  // handles the transportOption fetch killswitch
  const availableOptions = showAvailableReturnLabels ? options : backupLabelMethods;
  // handles the UPS_PICKUP activation killswitch
  const finalOptions = arePickupReturnsAuthorized ? availableOptions : availableOptions.filter(option => option !== PROVIDE_LABEL_OPTIONS.PICKUP);

  useEffect(() => {
    if (!isLoading) {
      track(() => [
        evReturnTransportationOptions,
        {
          availableReturnLabels: availableOptions,
          displayedReturnLabels: finalOptions,
          arePickupReturnsAuthorized: arePickupReturnsAuthorized
        }
      ]);
    }
  }, [availableOptions, finalOptions, arePickupReturnsAuthorized, isLoading]);

  return (
    <div className={isPrivateLabel ? cn(css.container, css.privateLabel) : css.container}>
      {isLoading ? (
        <SmallLoader />
      ) : (
        <>
          {finalOptions.map(option => {
            const Option = SHIPPING_METHOD_OPTIONS[option];
            return !!Option && <Option {...optionProps} key={option} />;
          })}
        </>
      )}
    </div>
  );
};

export default ShippingMethodChooser;
